import React, { useState, useMemo, useEffect, useCallback, useRef } from "react"
import { Button } from "@/components/ui/button"
import { FilePlus, FolderPlus, Loader2, ChevronDown, ChevronUp, Maximize2, PanelRight } from "lucide-react"
import { EntityContextMenu, type EntityContextMenuEntry } from "@/components/features/entity-context-menu"
import { getDocuments, fetchWithAuth, looksLikeDatabaseUnavailableError } from "@/lib/api"
import { extractTodoIdFromPath, inferBasecampRecordTypeFromDocument } from "@/lib/todo-id-from-path"
import { formatTimeAgo } from "@/components/layout/document-viewer.utils"
import { resolveBasecampAppUrl, buildBasecampWebUrl, normalizeBasecampWebHost } from "@/lib/basecamp-external-url"
import { useIntersectionObserver } from "@/hooks/use-intersection-observer"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { StorageKeys, StorageSchemas } from "@/lib/storage"
import { testIds } from "@/lib/test-ids"
import { normalizeFolderStoragePath } from "@/lib/app-routes"

import type {
  Document,
  SelectedItem,
  DocumentListProps,
  FilterType,
  ListSortColumn,
  SortDir,
  ViewMode,
  TodoFolderStripData,
  ExplorerFolder,
  DocumentListOrderedItem,
} from "./document-list.types"
import { getListTypeLabel, getSortColumnLabel } from "./document-list.labels"
import {
  DOCUMENT_LIST_COLS_CLASS,
  defaultSortDirForColumn,
  documentMatchesPanelSearch,
  folderSelection,
  selectionLivesInFolder,
  documentUrlStem,
  isReadmeDocument,
  toApiDocumentSort,
} from "./document-list.utils"
import {
  FOLDER_CARD_GRID_CLASS,
  LoadMoreTrigger,
  DocumentCard,
  DocumentGridCard,
  DocumentListRow,
  FolderCard,
  FolderGridCard,
  FolderListRow,
} from "./document-list-items"
import { DocumentListFilterRow, DocumentListToolbar } from "./document-list-toolbar"
import { PanelSearchBar } from "@/components/ui/panel-search-bar"
import { usePanelSearch } from "@/hooks/use-panel-search"
import { Panel, PanelGroup } from "react-resizable-panels"
import { IndexSurfaceShell, SecondaryPreviewResizeHandle } from "@/components/integrations/_shared"
import { ListSurfaceStatusBar } from "@/components/ui/list-surface-status-bar"
import { useLocation, useNavigate } from "react-router-dom"

export type { SelectedItem, ExplorerFolder, DocumentListOrderedItem } from "./document-list.types"

const PROJECT_ID_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function pickBrowseQuery(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams()
  const q = params.get('q')
  if (q) next.set('q', q)
  return next
}

function isProjectIdFilter(projectFilter: string | null | undefined): boolean {
  return !!(projectFilter && PROJECT_ID_UUID_RE.test(projectFilter))
}
export { DocumentPreviewPane } from "./document-list-preview-pane"

export function DocumentList({
  onDocumentClick,
  onNewDocument,
  projectFilter,
  activeDocumentId,
  onFolderClick,
  workspaceColor,
  onGoBack,
  onGoForward,
  canGoBack = false,
  canGoForward = false,
  selectedItem: controlledSelectedItem,
  onItemSelect,
  onFolderContextMenu,
  onDocumentContextMenu,
  previewPaneOpen = false,
  onTogglePreviewPane,
  workspaces = [],
  preview = null,
  onPanelSearchChange,
}: DocumentListProps) {
  const folderFilter = useMemo(
    () =>
      projectFilter && !isProjectIdFilter(projectFilter)
        ? normalizeFolderStoragePath(projectFilter, workspaces) ?? projectFilter
        : projectFilter,
    [projectFilter, workspaces],
  )

  const [typeFilter, setTypeFilter] = useState<FilterType>("all")
  /** Unfiltered document_type set for the current folder - keeps type chips visible while filtered. */
  const [folderTypeInventory, setFolderTypeInventory] = useState<string[]>([])
  const [authorFilter, setAuthorFilter] = useState<string>("all")
  const [statusFilter, setStatusFilter] = useState<"active" | "all">("active") // "active" = active+completed, "all" = include archived
  /** Default = former "Most Recent" (Modified desc). */
  const [sortBy, setSortBy] = useState<ListSortColumn>("modified")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [showAuthorMenu, setShowAuthorMenu] = useState(false)
  const [showSortMenu, setShowSortMenu] = useState(false)
  const apiSort = useMemo(() => toApiDocumentSort(sortBy, sortDir), [sortBy, sortDir])

  const handleSortColumnClick = useCallback(
    (column: ListSortColumn) => {
      if (sortBy === column) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
      } else {
        setSortBy(column)
        setSortDir(defaultSortDirForColumn(column))
      }
    },
    [sortBy],
  )
  const [viewMode, setViewMode] = usePersistedState<ViewMode>({
    key: StorageKeys.DOCUMENTS_FOLDER_VIEW_MODE,
    defaultValue: 'card',
    schema: StorageSchemas.viewMode,
  })

  // Real data from API
  const [documents, setDocuments] = useState<Document[]>([])
  /** Folder / project id the current `documents` array was loaded for (avoids mixing paths during in-flight navigations). */
  const [committedFolder, setCommittedFolder] = useState<string | null>(() => folderFilter ?? null)
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Folders fetched from filesystem
  const [filesystemFolders, setFilesystemFolders] = useState<ExplorerFolder[]>([])

  // Ref to track last API call parameters to prevent duplicate calls
  const lastApiCallRef = useRef<string>("")
  const lastLoadMoreRef = useRef<string>("")

  // Refresh trigger for post-delete sync (increment to force refetch)
  const [listRefreshKey, setListRefreshKey] = useState(0)

  // Ref for active document scroll-to functionality
  const activeDocumentRef = useRef<HTMLElement | null>(null)

  // Selection state — controlled from parent when onItemSelect is provided, otherwise internal
  const [internalSelectedItem, setInternalSelectedItem] = useState<SelectedItem | null>(null)
  const selectedItem = onItemSelect !== undefined ? (controlledSelectedItem ?? null) : internalSelectedItem
  const setSelectedItem = useCallback((item: SelectedItem | null) => {
    if (onItemSelect !== undefined) {
      onItemSelect(item)
    } else {
      setInternalSelectedItem(item)
    }
  }, [onItemSelect])
  const contentAreaRef = useRef<HTMLDivElement | null>(null)

  const { searchOpen, searchQuery, setSearchQuery, inputRef, close } = usePanelSearch()
  const navigate = useNavigate()
  const location = useLocation()
  const lastNotifiedPanelSearchRef = useRef<string | null>(null)
  const onPanelSearchChangeRef = useRef(onPanelSearchChange)
  onPanelSearchChangeRef.current = onPanelSearchChange
  useEffect(() => {
    const q = searchOpen ? searchQuery.trim() : ''
    if (lastNotifiedPanelSearchRef.current !== q) {
      lastNotifiedPanelSearchRef.current = q
      onPanelSearchChangeRef.current?.(q)
    }
    const next = new URLSearchParams()
    if (q) next.set('q', q)
    const current = new URLSearchParams(location.search.startsWith('?') ? location.search.slice(1) : location.search)
    if (next.toString() === pickBrowseQuery(current).toString()) return
    const path = `${location.pathname}${next.toString() ? `?${next.toString()}` : ''}`
    navigate(path, { replace: true })
  }, [searchOpen, searchQuery, location.pathname, location.search, navigate])

  // Listen for document-deleted / MCP content-revision to sync UI without full refresh
  useEffect(() => {
    const handler = () => {
      lastApiCallRef.current = ""
      setListRefreshKey(k => k + 1)
      setSelectedItem(null) // Clear selection so info pane doesn't show deleted doc
    }
    const revisionHandler = () => {
      lastApiCallRef.current = ""
      setListRefreshKey(k => k + 1)
    }
    window.addEventListener('document-deleted', handler)
    window.addEventListener('flownotes-content-revision', revisionHandler)
    window.addEventListener('document-renamed', revisionHandler)
    window.addEventListener('folder-renamed', revisionHandler)
    window.addEventListener('document-created', revisionHandler)
    window.addEventListener('document-moved', revisionHandler)
    window.addEventListener('folder-created', revisionHandler)
    window.addEventListener('folder-deleted', revisionHandler)
    return () => {
      window.removeEventListener('document-deleted', handler)
      window.removeEventListener('flownotes-content-revision', revisionHandler)
      window.removeEventListener('document-renamed', revisionHandler)
      window.removeEventListener('folder-renamed', revisionHandler)
      window.removeEventListener('document-created', revisionHandler)
      window.removeEventListener('document-moved', revisionHandler)
      window.removeEventListener('folder-created', revisionHandler)
      window.removeEventListener('folder-deleted', revisionHandler)
    }
  }, [setSelectedItem])

  // Follow the open folder when it (or an ancestor) is renamed, moved, or deleted.
  useEffect(() => {
    const current = (folderFilter && !isProjectIdFilter(folderFilter) ? folderFilter : '')
      .replace(/\/+$/, '')
    if (!current || !onFolderClick) return
    const isAtOrUnder = (base: string) => current === base || current.startsWith(`${base}/`)
    const onPathChanged = (event: Event) => {
      const { oldPath, newPath } = (event as CustomEvent<{ oldPath?: string; newPath?: string }>).detail ?? {}
      if (!oldPath || !newPath || !isAtOrUnder(oldPath)) return
      onFolderClick(`${newPath}${current.slice(oldPath.length)}`)
    }
    const onDeleted = (event: Event) => {
      const deletedPath = (event as CustomEvent<{ path?: string }>).detail?.path
      if (!deletedPath || !isAtOrUnder(deletedPath)) return
      const parent = deletedPath.split('/').slice(0, -1).join('/')
      if (parent) onFolderClick(parent)
    }
    window.addEventListener('folder-renamed', onPathChanged)
    window.addEventListener('folder-moved', onPathChanged)
    window.addEventListener('folder-deleted', onDeleted)
    return () => {
      window.removeEventListener('folder-renamed', onPathChanged)
      window.removeEventListener('folder-moved', onPathChanged)
      window.removeEventListener('folder-deleted', onDeleted)
    }
  }, [folderFilter, onFolderClick])

  // Scroll to active document when activeDocumentId changes
  useEffect(() => {
    if (activeDocumentId && activeDocumentRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        activeDocumentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }, 100)
    }
  }, [activeDocumentId, documents])

  // Clear selection when the main column changes, unless the selection is a
  // direct child of the folder we just opened (preview drill).
  const selectedItemRef = useRef(selectedItem)
  selectedItemRef.current = selectedItem
  useEffect(() => {
    if (!selectionLivesInFolder(selectedItemRef.current, folderFilter)) {
      setSelectedItem(null)
    }
    setTypeFilter("all")
    setFolderTypeInventory([])
  }, [folderFilter])

  // Cache type inventory from unfiltered loads so chips stay when typeFilter is applied
  useEffect(() => {
    if (typeFilter !== "all") return
    if (committedFolder !== folderFilter) return
    const types = [
      ...new Set(
        documents
          .map((d) => d.type)
          .filter((t): t is string => typeof t === "string" && t.length > 0 && t !== "all"),
      ),
    ].sort()
    setFolderTypeInventory(types)
  }, [documents, typeFilter, committedFolder, folderFilter])

  // Extract subfolders and separate folders from files
  // Merge folders from filesystem with folders extracted from file paths
  // NOTE: must be declared before the keyboard useEffect below to avoid TDZ crash
  const { folders, files } = useMemo(() => {
    const docsForList = committedFolder === folderFilter ? documents : []

    if (!folderFilter || isProjectIdFilter(folderFilter)) {
      return { folders: [] as ExplorerFolder[], files: docsForList }
    }

    const folderMap = new Map<string, ExplorerFolder>()
    const upsertFolder = (
      path: string,
      name: string,
      candidateIso: string | null | undefined,
      stats?: Pick<ExplorerFolder, 'fileCount' | 'folderCount' | 'hasSoul' | 'contentTypes' | 'tags'>,
    ) => {
      const candMs = candidateIso ? new Date(candidateIso).getTime() : Number.NaN
      const prev = folderMap.get(path)
      if (!prev) {
        folderMap.set(path, {
          name,
          path,
          maxUpdatedAt: Number.isFinite(candMs) ? new Date(candMs).toISOString() : null,
          fileCount: stats?.fileCount,
          folderCount: stats?.folderCount,
          hasSoul: stats?.hasSoul,
          contentTypes: stats?.contentTypes,
          tags: stats?.tags,
        })
        return
      }
      if (Number.isFinite(candMs)) {
        const prevMs = prev.maxUpdatedAt ? new Date(prev.maxUpdatedAt).getTime() : -Infinity
        if (candMs > prevMs) prev.maxUpdatedAt = new Date(candMs).toISOString()
      }
      if (typeof stats?.fileCount === 'number') prev.fileCount = stats.fileCount
      if (typeof stats?.folderCount === 'number') prev.folderCount = stats.folderCount
      if (typeof stats?.hasSoul === 'boolean') prev.hasSoul = stats.hasSoul
      if (stats?.contentTypes) prev.contentTypes = stats.contentTypes
      if (stats?.tags) prev.tags = stats.tags
    }

    for (const folder of filesystemFolders) {
      upsertFolder(folder.path, folder.name, folder.maxUpdatedAt, {
        fileCount: folder.fileCount,
        folderCount: folder.folderCount,
        hasSoul: folder.hasSoul,
        contentTypes: folder.contentTypes,
        tags: folder.tags,
      })
    }

    const currentFolderPath = folderFilter
    const fileList: Document[] = []

    for (const doc of docsForList) {
      const filePath = (doc as Document).filePath || (doc as Document).path
      if (!filePath) {
        fileList.push(doc)
        continue
      }

      const relativePath = filePath.startsWith(currentFolderPath + '/')
        ? filePath.slice(currentFolderPath.length + 1)
        : filePath

      const pathParts = relativePath.split('/').filter(p => p)

      if (pathParts.length > 1) {
        const subfolderName = pathParts[0]
        const subfolderPath = `${currentFolderPath}/${subfolderName}`
        upsertFolder(subfolderPath, subfolderName, doc.sortUpdatedAt)
      } else if (pathParts.length === 1) {
        fileList.push(doc)
      } else {
        fileList.push(doc)
      }
    }

    const folderList = [...folderMap.values()]
    const fileStems = new Set(
      fileList
        .map((doc) => documentUrlStem((doc as Document).filePath || (doc as Document).path))
        .filter((stem): stem is string => Boolean(stem))
    )
    for (const folder of folderList) {
      if (fileStems.has(folder.name)) folder.urlCollision = true
    }

    if (import.meta.env.DEV) {
      console.log('[DocumentList] Folder extraction result:', {
        folder: folderFilter,
        totalDocuments: docsForList.length,
        foldersFound: folderList.length,
        filesFound: fileList.length,
        filesystemFolders: filesystemFolders.length,
        filePaths: docsForList.slice(0, 5).map((d) => (d as Document).filePath || (d as Document).path),
      })
    }

    return { folders: folderList, files: fileList }
  }, [documents, folderFilter, filesystemFolders, committedFolder])

  const orderedItems = useMemo((): DocumentListOrderedItem[] => {
    const rowItems: DocumentListOrderedItem[] = [
      ...folders.map((folder) => ({ kind: "folder" as const, folder })),
      ...files.map((doc) => ({ kind: "document" as const, doc })),
    ]

    const sortMs = (item: DocumentListOrderedItem): number | null => {
      if (item.kind === "folder") {
        const iso = item.folder.maxUpdatedAt
        if (!iso) return null
        const t = new Date(iso).getTime()
        return Number.isFinite(t) ? t : null
      }
      const iso = item.doc.sortUpdatedAt
      if (!iso) return null
      const t = new Date(iso).getTime()
      return Number.isFinite(t) ? t : null
    }

    const displayTitle = (item: DocumentListOrderedItem): string =>
      item.kind === "folder" ? item.folder.name : item.doc.title

    const displayType = (item: DocumentListOrderedItem): string =>
      item.kind === "folder" ? "" : getListTypeLabel(item.doc.type)

    const displayAuthor = (item: DocumentListOrderedItem): string =>
      item.kind === "folder" ? "" : item.doc.updatedBy || ""

    const titleCompare = (a: DocumentListOrderedItem, b: DocumentListOrderedItem) => {
      const ca = displayTitle(a).localeCompare(displayTitle(b), undefined, { sensitivity: "base" })
      if (ca !== 0) return ca
      if (a.kind === b.kind) return 0
      return a.kind === "folder" ? -1 : 1
    }

    const dirMul = sortDir === "asc" ? 1 : -1
    const sorted = [...rowItems]

    const isReadmeItem = (item: DocumentListOrderedItem): boolean =>
      item.kind === "document" && isReadmeDocument(item.doc)

    sorted.sort((a, b) => {
      // Folders first, then README.md index hubs, then everything else.
      if (a.kind !== b.kind) return a.kind === "folder" ? -1 : 1
      if (a.kind === "document" && b.kind === "document") {
        const ar = isReadmeItem(a)
        const br = isReadmeItem(b)
        if (ar !== br) return ar ? -1 : 1
      }

      if (sortBy === "modified") {
        const ma = sortMs(a)
        const mb = sortMs(b)
        if (ma === null && mb === null) return titleCompare(a, b)
        if (ma === null) return 1
        if (mb === null) return -1
        if (ma !== mb) return (ma - mb) * dirMul
        return titleCompare(a, b)
      }

      let cmp = 0
      if (sortBy === "name") {
        cmp = displayTitle(a).localeCompare(displayTitle(b), undefined, { sensitivity: "base" })
      } else if (sortBy === "type") {
        cmp = displayType(a).localeCompare(displayType(b), undefined, { sensitivity: "base" })
      } else if (sortBy === "lines" || sortBy === "size") {
        const metric = (item: DocumentListOrderedItem) =>
          item.kind === "folder" ? 0 : (sortBy === "lines" ? item.doc.lineCount ?? 0 : item.doc.byteSize ?? 0)
        cmp = metric(a) - metric(b)
      } else {
        cmp = displayAuthor(a).localeCompare(displayAuthor(b), undefined, { sensitivity: "base" })
      }
      if (cmp !== 0) return cmp * dirMul
      return titleCompare(a, b)
    })

    return sorted
  }, [folders, files, sortBy, sortDir])

  const filteredOrderedItems = useMemo((): DocumentListOrderedItem[] => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return orderedItems
    return orderedItems.filter((item) => {
      if (item.kind === "folder") {
        return (
          item.folder.name.toLowerCase().includes(q) ||
          item.folder.path.toLowerCase().includes(q)
        )
      }
      return documentMatchesPanelSearch(item.doc, q)
    })
  }, [orderedItems, searchQuery])

  // Basecamp context strip: when viewing a todo folder (todos/{todoId}-*), show todo title + link
  const todoIdFromPath = useMemo(() => {
    if (!folderFilter || isProjectIdFilter(folderFilter)) return null
    return extractTodoIdFromPath(folderFilter)
  }, [folderFilter])

  const workSpecDocumentId = useMemo(() => {
    if (!todoIdFromPath) return null
    const readme = files.find((f) => {
      const p = f.filePath || f.path
      return typeof p === "string" && /(^|\/)README\.md$/i.test(p)
    })
    return readme?.id ?? null
  }, [files, todoIdFromPath])

  const [todoStripData, setTodoStripData] = useState<TodoFolderStripData | null>(null)
  useEffect(() => {
    if (!todoIdFromPath) {
      setTodoStripData(null)
      return
    }
    let cancelled = false

    function appUrlFromCachedData(cachedData: unknown): string | null {
      if (!cachedData) return null
      if (typeof cachedData === "object" && cachedData !== null && "app_url" in cachedData) {
        const u = (cachedData as { app_url?: string }).app_url
        return typeof u === "string" && u.length > 0 ? u : null
      }
      if (typeof cachedData === "string") {
        try {
          const parsed = JSON.parse(cachedData) as { app_url?: string }
          return typeof parsed?.app_url === "string" ? parsed.app_url : null
        } catch {
          return null
        }
      }
      return null
    }

    ;(async () => {
      try {
        let title = ""
        let appUrl: string | null = null
        let completed = false
        let statusLabel = "Active"
        let recordType: 'todo' | 'todolist' = 'todo'

        const docRes = await fetchWithAuth(`/api/basecamp/todo-documents/${todoIdFromPath}?limit=10`)
        const docData = docRes.ok ? await docRes.json() : null
        const docs = Array.isArray(docData?.documents) ? docData.documents : []

        const folderPrefix = `/todos/${todoIdFromPath}-`
        const readmeDoc = docs.find((d: { filePath?: string }) => {
          const p = d.filePath || (d as { path?: string }).path
          return typeof p === "string" && p.includes(folderPrefix) && /\/README\.md$/i.test(p)
        }) as
          | {
              title?: string
              filePath?: string
              excerpt?: string
              frontmatter?: Record<string, unknown>
              projectName?: string | null
            }
          | undefined

        // FlowNotes project display name, resolved from the same todo-documents lookup (no extra request).
        const projectName: string | null =
          readmeDoc?.projectName ??
          (docs.find(
            (d: { projectName?: string | null }) =>
              typeof d.projectName === "string" && d.projectName.trim().length > 0,
          ) as { projectName?: string | null } | undefined)?.projectName ??
          null

        if (readmeDoc) {
          title = (readmeDoc.title && String(readmeDoc.title).trim()) || ""
          recordType = inferBasecampRecordTypeFromDocument(todoIdFromPath, {
            filePath: readmeDoc.filePath,
            excerpt: readmeDoc.excerpt,
            frontmatter: readmeDoc.frontmatter,
          })
          const fm = readmeDoc.frontmatter || {}
          const text = `${readmeDoc.excerpt || ""}\n${String(fm.source_url || "")}\n${String(fm.url || "")}`
          const todolistMatch = text.match(
            new RegExp(`https://(?:3|app)\\.basecamp\\.com/\\d+/buckets/\\d+/todolists/${todoIdFromPath}\\b`, "i"),
          )
          const todoMatch = text.match(
            new RegExp(`https://(?:3|app)\\.basecamp\\.com/\\d+/buckets/\\d+/todos/\\d+\\b`, "i"),
          )
          appUrl =
            (recordType === "todolist" ? todolistMatch?.[0] : null) ||
            todoMatch?.[0] ||
            (typeof fm.source_url === "string" ? fm.source_url : null) ||
            (typeof fm.url === "string" ? fm.url : null)
        }

        const searchRes = await fetchWithAuth(`/api/basecamp/search-todo/${todoIdFromPath}`)
        const searchJson = searchRes.ok ? await searchRes.json() : null
        if (!cancelled && searchJson?.found && searchJson?.todo) {
          const t = searchJson.todo as {
            title?: string
            completed?: boolean
            status?: string
            projectId?: string
            recordType?: 'todo' | 'todolist'
            cachedData?: unknown
          }
          if (!title) {
            title = (t.title && String(t.title).trim()) || ""
          }
          // search-todo is authoritative (Basecamp cache /app_url). Doc inference
          // can mislabel escalated todo folders as todolists when frontmatter is
          // missing from the documents payload - always prefer search when present.
          if (t.recordType === 'todolist' || t.recordType === 'todo') {
            recordType = t.recordType
          }
          completed = !!t.completed
          const rawStatus = (t.status && String(t.status)) || "active"
          statusLabel = completed ? "Completed" : rawStatus.replace(/_/g, " ")
          if (t.projectId) {
            if (!appUrl) {
              appUrl = resolveBasecampAppUrl({
                recordId: todoIdFromPath,
                projectId: String(t.projectId),
                recordType,
              })
            }
          }
          const fromCache = appUrlFromCachedData(t.cachedData)
          if (fromCache && t.projectId) {
            appUrl = resolveBasecampAppUrl({
              recordId: todoIdFromPath,
              projectId: String(t.projectId),
              recordType,
              candidateUrl: fromCache,
            })
          } else if (fromCache) {
            appUrl = fromCache
          }
        }

        if (!title && docs.length) {
          const first = docs[0] as { title?: string }
          title = (first?.title && String(first.title).trim()) || ""
        }
        if (!appUrl && docs.length) {
          const first = docs[0] as { frontmatter?: { source_url?: string; url?: string } }
          const url = first?.frontmatter?.source_url ?? first?.frontmatter?.url ?? null
          if (typeof url === "string" && url.length > 0) appUrl = normalizeBasecampWebHost(url)
        }

        if (!title) {
          title = folderFilter?.split("/").pop() ?? `Todo ${todoIdFromPath}`
        }

        let lastCommentPreview: string | null = null
        // `/ai/todo/comments` only accepts /todos/{id} URLs (Basecamp todolists don't expose
        // comments) - skip the call for todolist folders instead of letting it 400.
        if (appUrl && recordType === "todo") {
          try {
            const cRes = await fetchWithAuth(`/ai/todo/comments`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ todoUrl: appUrl }),
            })
            if (cRes.ok) {
              const cData = await cRes.json()
              const comments = (cData.comments ?? cData) as unknown
              const arr = Array.isArray(comments) ? comments : []
              if (arr.length) {
                const last = arr[arr.length - 1] as { content?: string; body?: string }
                const raw = String(last?.content ?? last?.body ?? "")
                const text = raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
                lastCommentPreview = text.length > 120 ? `${text.slice(0, 117)}…` : text || null
              }
            }
          } catch {
            /* optional */
          }
        }

        let projectId: string | null = null
        if (!cancelled && searchJson?.found && searchJson?.todo?.projectId) {
          projectId = String(searchJson.todo.projectId)
        }

        if (appUrl && projectId) {
          appUrl = resolveBasecampAppUrl({
            recordId: todoIdFromPath,
            projectId,
            recordType,
            candidateUrl: appUrl,
          })
        }

        if (!cancelled) {
          setTodoStripData({
            title,
            appUrl,
            completed,
            statusLabel,
            lastCommentPreview,
            projectId,
            recordType,
            projectName,
          })
        }
      } catch {
        if (!cancelled) setTodoStripData(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [todoIdFromPath, folderFilter])

  // Keyboard navigation: arrows select, Enter opens, Escape clears
  useEffect(() => {
    const el = contentAreaRef.current
    if (!el) return

    const navItems: SelectedItem[] = filteredOrderedItems.map((item) =>
      item.kind === "folder"
        ? folderSelection(item.folder)
        : { kind: "document" as const, doc: item.doc },
    )

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (searchOpen) {
          close()
          e.stopPropagation()
          return
        }
        setSelectedItem(null)
        return
      }

      if (navItems.length === 0) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const currentIdx = selectedItem
          ? navItems.findIndex(item =>
              item.kind === 'folder' && selectedItem.kind === 'folder'
                ? item.path === selectedItem.path
                : item.kind === 'document' && selectedItem.kind === 'document'
                  ? item.doc.id === selectedItem.doc.id
                  : false
            )
          : -1
        const nextIdx = e.key === 'ArrowDown'
          ? (currentIdx + 1) % navItems.length
          : (currentIdx - 1 + navItems.length) % navItems.length
        setSelectedItem(navItems[nextIdx])
        return
      }

      if (e.key === 'Enter' && selectedItem) {
        e.preventDefault()
        if (selectedItem.kind === 'folder') {
          const modifier = e.metaKey || e.ctrlKey
            ? ({ metaKey: e.metaKey, ctrlKey: e.ctrlKey } as React.MouseEvent)
            : undefined
          onFolderClick?.(selectedItem.path, modifier)
          window.dispatchEvent(new CustomEvent('show-folder-in-browse', { detail: { folderPath: selectedItem.path } }))
        } else {
          onDocumentClick?.(selectedItem.doc.id, e.metaKey || e.ctrlKey)
        }
      }
    }

    el.addEventListener('keydown', handleKey)
    return () => el.removeEventListener('keydown', handleKey)
  }, [selectedItem, filteredOrderedItems, searchOpen, close, onFolderClick, onDocumentClick])

  // Fetch initial documents from API
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[DocumentList] useEffect triggered with:', { typeFilter, authorFilter, sortBy, sortDir, folderFilter })
    }

    // Reset load more tracking when filters change
    lastLoadMoreRef.current = ""

    let cancelled = false

    const fetchDocuments = async () => {
      // At root level (no projectFilter), don't fetch documents - only show folders
      if (!folderFilter) {
        if (import.meta.env.DEV) {
          console.log('[DocumentList] Root level - skipping document fetch, showing folders only')
        }
        setDocuments([])
        setTotalDocuments(0)
        setHasMore(false)
        setLoading(false)
        setCommittedFolder(null)
        return
      }

      const isProjectId = isProjectIdFilter(folderFilter)

      // Create a unique key for this API call to prevent duplicates
      const apiCallKey = JSON.stringify({
        type: typeFilter === 'all' ? undefined : typeFilter,
        author: authorFilter === 'all' ? undefined : authorFilter,
        status: statusFilter,
        project: isProjectId ? (folderFilter ?? undefined) : undefined,
        folder: !isProjectId && folderFilter ? (folderFilter ?? undefined) : undefined,
        sort: apiSort,
        limit: 50, // Match actual API call limit
        offset: 0,
      })

      // Skip API call if parameters haven't changed
      if (lastApiCallRef.current === apiCallKey) {
        if (import.meta.env.DEV) {
          console.log('[DocumentList] Skipping duplicate API call with same parameters')
        }
        return
      }

      lastApiCallRef.current = apiCallKey

      setLoading(true)
      setError(null)
      setHasMore(true)

      try {
        if (import.meta.env.DEV) {
          console.log('[DocumentList] Making API call with params:', JSON.parse(apiCallKey))
        }

        const response = await getDocuments({
          type: typeFilter === 'all' ? undefined : typeFilter,
          author: authorFilter === 'all' ? undefined : authorFilter,
          project: isProjectId ? (folderFilter ?? undefined) : undefined,
          folder: !isProjectId && folderFilter ? (folderFilter ?? undefined) : undefined,
          sort: apiSort,
          limit: 50, // Increased initial load from 20 to 50 for faster display
          offset: 0,
          includeArchived: statusFilter === 'all',
        })

        const logData: any = {
          documentCount: response.documents.length,
          total: response.pagination.total,
          folder: folderFilter,
          documents: response.documents.map((d: any) => ({
            id: d.id,
            title: d.title,
            filePath: d.filePath || d.path
          }))
        }

        // Optional server-side debug payload (not sent by default)
        const responseWithDebug = response as typeof response & { debug?: unknown }
        if (responseWithDebug.debug) {
          logData.debug = responseWithDebug.debug
          console.log('[DocumentList] API debug:', responseWithDebug.debug)
        }

        if (import.meta.env.DEV) {
          console.log('[DocumentList] API response:', logData)
        }

        if (cancelled) return

        setDocuments(response.documents as Document[])
        setTotalDocuments(response.pagination.total)
        setHasMore(response.documents.length < response.pagination.total)
        setCommittedFolder(folderFilter)
      } catch (err) {
        console.error('Failed to fetch documents:', err)
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load documents')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    // Add debouncing to prevent excessive API calls
    const timeoutId = setTimeout(() => {
      void fetchDocuments()
    }, 300) // 300ms debounce

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      setLoading(false)
    }
  }, [typeFilter, authorFilter, statusFilter, apiSort, folderFilter, listRefreshKey])

  // Fetch subfolders from DB (same source as GET /api/documents?folder=...) so sidebar and list stay aligned
  useEffect(() => {
    let cancelled = false

    const fetchFolders = async () => {
      if (isProjectIdFilter(folderFilter)) {
        if (!cancelled) setFilesystemFolders([])
        return
      }

      try {
        let folders: ExplorerFolder[] = []

        if (!folderFilter) {
          const response = await fetchWithAuth('/api/documents/explorer-root-folders')
          if (!response.ok) throw new Error('Failed to fetch root folders')
          const data = await response.json()
          if (data.success && Array.isArray(data.folders)) {
            folders = data.folders.map((f: { name: string; path: string; maxUpdatedAt?: string | null }) => ({
              name: f.name,
              path: f.path,
              maxUpdatedAt: typeof f.maxUpdatedAt === 'string' ? f.maxUpdatedAt : null,
            }))
          }
        } else {
          const response = await fetchWithAuth(
            `/api/documents/explorer-subfolders?path=${encodeURIComponent(folderFilter)}`
          )
          if (!response.ok) throw new Error('Failed to fetch subfolders')
          const data = await response.json()
          if (data.success && Array.isArray(data.folders)) {
            folders = data.folders.map((f: {
              name: string
              path: string
              maxUpdatedAt?: string | null
              fileCount?: number
              folderCount?: number
              hasSoul?: boolean
              contentTypes?: string[]
              tags?: string[]
            }) => ({
              name: f.name,
              path: f.path,
              maxUpdatedAt: typeof f.maxUpdatedAt === 'string' ? f.maxUpdatedAt : null,
              fileCount: typeof f.fileCount === 'number' ? f.fileCount : undefined,
              folderCount: typeof f.folderCount === 'number' ? f.folderCount : undefined,
              hasSoul: f.hasSoul === true,
              contentTypes: Array.isArray(f.contentTypes) ? f.contentTypes.filter((t) => typeof t === 'string') : undefined,
              tags: Array.isArray(f.tags) ? f.tags.filter((t) => typeof t === 'string') : undefined,
            }))
          }
        }

        if (!cancelled) setFilesystemFolders(folders)
      } catch (err) {
        console.error('Failed to fetch explorer folders from DB:', err)
        if (!cancelled) setFilesystemFolders([])
      }
    }

    fetchFolders()

    return () => {
      cancelled = true
    }
  }, [folderFilter, listRefreshKey])

  // Load more documents (infinite scroll)
  const loadMoreDocuments = useCallback(async () => {
    // Don't load more if:
    // - Already loading more
    // - No more documents available
    // - Currently loading initial fetch
    // - No documents loaded yet (wait for initial fetch)
    if (loadingMore || !hasMore || loading || documents.length === 0) {
      return
    }

    const isProjectId = isProjectIdFilter(folderFilter)

    // Use current documents length directly (not ref) to ensure we have latest value
    const currentOffset = documents.length

    // Safety check: if offset is 0 but we have documents, something is wrong
    if (currentOffset === 0 && documents.length > 0) {
      console.warn('[DocumentList] Load more called with offset 0 but documents exist, skipping')
      return
    }

    // Create a unique key for this load more API call
    const loadMoreKey = JSON.stringify({
      type: typeFilter === 'all' ? undefined : typeFilter,
      author: authorFilter === 'all' ? undefined : authorFilter,
      project: isProjectId ? (folderFilter ?? undefined) : undefined,
      folder: !isProjectId && folderFilter ? (folderFilter ?? undefined) : undefined,
      sort: apiSort,
      limit: 20,
      offset: currentOffset,
    })

    // Skip API call if parameters haven't changed
    if (lastLoadMoreRef.current === loadMoreKey) {
      console.log('[DocumentList] Skipping duplicate load more API call')
      return
    }

    lastLoadMoreRef.current = loadMoreKey

    setLoadingMore(true)
    setError(null)

    try {
      console.log('[DocumentList] Making load more API call with offset:', currentOffset)

      const response = await getDocuments({
        type: typeFilter === 'all' ? undefined : typeFilter,
        author: authorFilter === 'all' ? undefined : authorFilter,
        project: isProjectId ? (folderFilter ?? undefined) : undefined,
        folder: !isProjectId && folderFilter ? (folderFilter ?? undefined) : undefined,
        sort: apiSort,
        limit: 50, // Increased pagination size from 20 to 50
        offset: currentOffset,
        includeArchived: statusFilter === 'all',
      })

      // Update documents and hasMore using the updated length
      setDocuments(prev => {
        // Deduplicate by document ID and filePath
        const existingIds = new Set(prev.map(doc => doc.id))
        const existingPaths = new Set(prev.map(doc => (doc as any).filePath || (doc as any).path).filter(Boolean))

        const newDocs = response.documents as Document[]
        const uniqueNewDocs = newDocs.filter(doc => {
          const docPath = (doc as any).filePath || (doc as any).path
          // Skip if ID already exists OR filePath already exists
          return !existingIds.has(doc.id) && (!docPath || !existingPaths.has(docPath))
        })

        const combined = [...prev, ...uniqueNewDocs]
        const newTotal = combined.length

        // Update hasMore based on the NEW document count, not old
        setHasMore(newTotal < response.pagination.total)

        return combined
      })
    } catch (err) {
      console.error('Failed to load more documents:', err)
      setError(err instanceof Error ? err.message : 'Failed to load more documents')
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, loading, typeFilter, authorFilter, folderFilter, apiSort, documents.length, statusFilter])

  // Ref for debouncing load more calls
  const loadMoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced load more function to prevent excessive calls
  const debouncedLoadMore = useCallback(() => {
    // Clear any existing timeout
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current)
    }

    // Set new timeout
    loadMoreTimeoutRef.current = setTimeout(() => {
      loadMoreDocuments()
    }, 500) // 500ms debounce for load more
  }, [loadMoreDocuments])

  // Intersection observer for infinite scroll
  // Increased rootMargin to trigger earlier, but with better debouncing
  // Only enable when we have documents loaded (prevents triggering before initial fetch completes)
  const { ref: loadMoreRef } = useIntersectionObserver({
    onIntersect: debouncedLoadMore,
    threshold: 0.1,
    rootMargin: '400px', // Increased from 200px to trigger earlier
    enabled: hasMore && !loadingMore && !loading && documents.length > 0
  })

  // Get unique authors from loaded documents
  const authors = useMemo(() => {
    const uniqueAuthors = new Set(documents.map(doc => doc.updatedBy).filter(Boolean))
    return ["all", ...Array.from(uniqueAuthors)]
  }, [documents])

  const handleReloadFolder = useCallback(() => {
    lastApiCallRef.current = ""
    setListRefreshKey((k) => k + 1)
  }, [])

  const filterKey = folderFilter ?? ''
  const committedKey = committedFolder ?? ''
  const listDataStale = filterKey !== committedKey
  /** Full-page spinner only when loading and we cannot show the previous folder's list (in-flight nav or initial empty). */
  const showFullPageLoading = loading && !error && (listDataStale || documents.length === 0)

  const canAcceptNativeTextDrop =
    !!folderFilter && !isProjectIdFilter(folderFilter) && folderFilter.trim().length > 0
  const [isNativeFolderDropActive, setIsNativeFolderDropActive] = useState(false)
  const [backgroundMenu, setBackgroundMenu] = useState<{ x: number; y: number } | null>(null)

  const openBackgroundMenu = useCallback((event: React.MouseEvent) => {
    const target = event.target
    if (!(target instanceof Element)) return
    if (target.closest('button, a, input, textarea, [role="button"], [role="menuitem"]')) return
    event.preventDefault()
    setBackgroundMenu({ x: event.clientX, y: event.clientY })
  }, [])

  const backgroundMenuEntries = useMemo((): EntityContextMenuEntry[] => {
    const folderPath = (
      folderFilter && !isProjectIdFilter(folderFilter) ? folderFilter : ''
    )
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
    return [
      {
        label: 'New document',
        icon: FilePlus,
        onSelect: () => {
          if (folderPath) {
            window.dispatchEvent(
              new CustomEvent('create-file-at-path', { detail: { folderPath } }),
            )
          } else {
            onNewDocument?.()
          }
        },
      },
      {
        label: 'New folder',
        icon: FolderPlus,
        onSelect: () => {
          window.dispatchEvent(
            new CustomEvent('create-folder-at-path', { detail: { parentPath: folderPath } }),
          )
        },
      },
      { separator: true },
      {
        label: previewPaneOpen ? 'Hide preview pane' : 'Show preview pane',
        icon: PanelRight,
        disabled: !onTogglePreviewPane,
        onSelect: () => onTogglePreviewPane?.(),
      },
      {
        label: 'Focus view',
        icon: Maximize2,
        onSelect: () => {
          window.dispatchEvent(new CustomEvent('flownotes-focus-view'))
        },
      },
    ]
  }, [folderFilter, onNewDocument, onTogglePreviewPane, previewPaneOpen])

  return (
    <IndexSurfaceShell
      data-testid={testIds.documentList.container}
      slotTestIds={{
        header: testIds.documentList.headerSlot,
        search: testIds.documentList.searchSlot,
        footer: testIds.documentList.footerSlot,
      }}
      header={
      <DocumentListToolbar
        projectFilter={folderFilter}
        onFolderClick={onFolderClick}
        onGoBack={onGoBack}
        onGoForward={onGoForward}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onNewDocument={onNewDocument}
        currentFolderPath={
          folderFilter && !isProjectIdFilter(folderFilter) ? folderFilter : null
        }
        onTogglePreviewPane={onTogglePreviewPane}
        previewPaneOpen={previewPaneOpen}
        onReload={handleReloadFolder}
        isReloading={loading}
        workspaceColor={workspaceColor}
        todoIdFromPath={todoIdFromPath}
        todoStripData={todoStripData}
        workSpecDocumentId={workSpecDocumentId}
        onOpenWorkSpec={
          workSpecDocumentId && onDocumentClick
            ? () => onDocumentClick(workSpecDocumentId)
            : undefined
        }
        folderTypeInventory={folderTypeInventory}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        authorFilter={authorFilter}
        setAuthorFilter={setAuthorFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        showAuthorMenu={showAuthorMenu}
        setShowAuthorMenu={setShowAuthorMenu}
        showSortMenu={showSortMenu}
        setShowSortMenu={setShowSortMenu}
        authors={authors}
        sortBy={sortBy}
        sortDir={sortDir}
        setSortBy={setSortBy}
        setSortDir={setSortDir}
        viewMode={viewMode}
        setViewMode={setViewMode}
        documentsLength={documents.length}
        totalDocuments={totalDocuments}
        workspaces={workspaces}
      />
      }
      search={
        searchOpen ? (
        <PanelSearchBar
          inputRef={inputRef}
          value={searchQuery}
          onChange={setSearchQuery}
          onClose={close}
          placeholder="filter by title, filename..."
          resultCount={filteredOrderedItems.length}
          totalCount={orderedItems.length}
          data-testid={testIds.documentList.panelSearch}
        />
        ) : null
      }
      footer={
        <ListSurfaceStatusBar
          includePreviewTip
          data-testid={testIds.documentList.statusBar}
          helpTestId={testIds.documentList.statusBarHelp}
          metaTestId={testIds.documentList.statusBarMeta}
          trailing={
            <span className="tabular-nums">
              {documents.length} document{documents.length === 1 ? '' : 's'}
            </span>
          }
        />
      }
    >
    <div
      className={`h-full flex min-h-0 flex-1 flex-col bg-background relative ${
        isNativeFolderDropActive
          ? 'ring-2 ring-inset ring-primary/70 bg-primary/5 cursor-copy'
          : ''
      }`}
      {...(canAcceptNativeTextDrop
        ? { 'data-folder-drop-zone': testIds.documentList.folderDropZone }
        : {})}
      onDragEnter={
        canAcceptNativeTextDrop
          ? (e) => {
              if (!e.dataTransfer.types.includes('Files')) return
              e.preventDefault()
              e.stopPropagation()
              setIsNativeFolderDropActive(true)
            }
          : undefined
      }
      onDragOver={
        canAcceptNativeTextDrop
          ? (e) => {
              if (!e.dataTransfer.types.includes('Files')) return
              e.preventDefault()
              e.stopPropagation()
              e.dataTransfer.dropEffect = 'copy'
              if (!isNativeFolderDropActive) setIsNativeFolderDropActive(true)
            }
          : undefined
      }
      onDragLeave={
        canAcceptNativeTextDrop
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsNativeFolderDropActive(false)
              }
            }
          : undefined
      }
      onDrop={
        canAcceptNativeTextDrop
          ? (e) => {
              if (!e.dataTransfer.files?.length) return
              e.preventDefault()
              e.stopPropagation()
              setIsNativeFolderDropActive(false)
              window.dispatchEvent(
                new CustomEvent('import-text-drop-at-path', {
                  detail: {
                    folderPath: folderFilter,
                    files: Array.from(e.dataTransfer.files),
                  },
                }),
              )
            }
          : undefined
      }
    >
      {isNativeFolderDropActive ? (
        <div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary/60"
          data-testid={testIds.documentList.folderDropZone}
          aria-hidden
        >
          <div className="rounded-md bg-background/90 px-4 py-2 text-sm font-medium text-foreground shadow-sm">
            Drop notes or images to import into this folder
          </div>
        </div>
      ) : null}
      <PanelGroup direction="horizontal" className="flex min-h-0 flex-1">
        <Panel minSize={30} className="flex min-h-0 flex-col overflow-hidden">
      <DocumentListFilterRow
        folderTypeInventory={folderTypeInventory}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        authorFilter={authorFilter}
        setAuthorFilter={setAuthorFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        showAuthorMenu={showAuthorMenu}
        setShowAuthorMenu={setShowAuthorMenu}
        showSortMenu={showSortMenu}
        setShowSortMenu={setShowSortMenu}
        authors={authors}
        sortBy={sortBy}
        sortDir={sortDir}
        setSortBy={setSortBy}
        setSortDir={setSortDir}
        viewMode={viewMode}
        setViewMode={setViewMode}
        documentsLength={documents.length}
        totalDocuments={totalDocuments}
      />
      <div
        ref={contentAreaRef}
        tabIndex={0}
        className="flex-1 overflow-y-auto outline-none focus:ring-0"
        onContextMenu={openBackgroundMenu}
      >
        {showFullPageLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-2" />
            <p className="text-muted-foreground">Loading documents...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-2">⚠️ {error}</p>
            <p className="text-sm text-muted-foreground">
              {import.meta.env.DEV
                ? "Make sure the backend server is running on port 3000."
                : looksLikeDatabaseUnavailableError(error)
                  ? "The API could not reach the database (often a short pooler or network blip). Wait a minute and retry; if it persists, check Supabase project status and Railway DATABASE_URL / pool settings."
                  : "Check your connection and try again. If the problem continues, the hosted API may be temporarily unavailable."}
            </p>
          </div>
        ) : orderedItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-2">No documents match your filters</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTypeFilter("all")
                setAuthorFilter("all")
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : filteredOrderedItems.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-2">No items match this search</p>
            <Button variant="ghost" size="sm" onClick={() => close()}>
              Clear search
            </Button>
          </div>
        ) : (
          <>
            {/* Conditional rendering based on viewMode */}
            {viewMode === 'card' && (
              <div className="p-6 space-y-4">
                {filteredOrderedItems.map((item, index) => {
                  if (item.kind === "folder") {
                    const { folder } = item
                    return (
                      <FolderCard
                        key={folder.path}
                        folder={folder}
                        color={workspaceColor}
                        isSelected={selectedItem?.kind === 'folder' && selectedItem.path === folder.path}
                        onClick={() => setSelectedItem(folderSelection(folder))}
                        onDoubleClick={(e) => {
                          onFolderClick?.(folder.path, e)
                          window.dispatchEvent(
                            new CustomEvent("show-folder-in-browse", {
                              detail: { folderPath: folder.path },
                            }),
                          )
                        }}
                        onContextMenu={onFolderContextMenu}
                      />
                    )
                  }
                  const doc = item.doc
                  const docId = doc.id
                  const docPath = doc.filePath || doc.path
                  const isDocActive = activeDocumentId === docId || activeDocumentId === docPath
                  const uniqueKey = `${doc.id}-${index}`
                  return (
                    <DocumentCard
                      key={uniqueKey}
                      document={doc}
                      onClick={() => setSelectedItem({ kind: "document", doc })}
                      onDoubleClick={(e) => {
                        const openInNewTab = e.metaKey || e.ctrlKey
                        onDocumentClick?.(doc.id, openInNewTab)
                      }}
                      onContextMenu={onDocumentContextMenu}
                      isActive={isDocActive}
                      isSelected={selectedItem?.kind === "document" && selectedItem.doc.id === doc.id}
                      innerRef={isDocActive ? activeDocumentRef : undefined}
                      data-testid={testIds.documentList.item(doc.id)}
                    />
                  )
                })}
              </div>
            )}

            {viewMode === 'grid' && (
              <div className="p-6">
                <div className="space-y-6">
                  {filteredOrderedItems.some((item) => item.kind === "folder") ? (
                    <div data-testid={testIds.documentList.folderGrid}>
                      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        Folders
                      </p>
                      <div className={FOLDER_CARD_GRID_CLASS}>
                        {filteredOrderedItems.map((item) => {
                          if (item.kind !== "folder") return null
                          const { folder } = item
                          return (
                            <FolderGridCard
                              key={folder.path}
                              folder={folder}
                              color={workspaceColor}
                              isSelected={selectedItem?.kind === "folder" && selectedItem.path === folder.path}
                              onClick={() => setSelectedItem(folderSelection(folder))}
                              onDoubleClick={(e) => onFolderClick?.(folder.path, e)}
                              onContextMenu={onFolderContextMenu}
                            />
                          )
                        })}
                      </div>
                    </div>
                  ) : null}
                  {filteredOrderedItems.some((item) => item.kind === "document") ? (
                    <div data-testid={testIds.documentList.fileGrid}>
                      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        Files
                      </p>
                      <div className={FOLDER_CARD_GRID_CLASS}>
                        {filteredOrderedItems.map((item, index) => {
                          if (item.kind !== "document") return null
                          const doc = item.doc
                          const docId = doc.id
                          const docPath = doc.filePath || doc.path
                          const isDocActive = activeDocumentId === docId || activeDocumentId === docPath
                          const uniqueKey = `${doc.id}-${index}`
                          return (
                            <DocumentGridCard
                              key={uniqueKey}
                              document={doc}
                              onClick={() => setSelectedItem({ kind: "document", doc })}
                              onDoubleClick={(e) => {
                                const openInNewTab = e.metaKey || e.ctrlKey
                                onDocumentClick?.(doc.id, openInNewTab)
                              }}
                              onContextMenu={onDocumentContextMenu}
                              isActive={isDocActive}
                              isSelected={selectedItem?.kind === "document" && selectedItem.doc.id === doc.id}
                              innerRef={isDocActive ? activeDocumentRef : undefined}
                              data-testid={testIds.documentList.itemGrid(doc.id)}
                            />
                          )
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {viewMode === 'list' && (
              <div className="overflow-auto min-h-0 flex-1">
                <div className="min-w-[46rem]">
                  {/* Column headers - shared grid with rows; click to sort */}
                  {filteredOrderedItems.length > 0 && (
                    <div
                      className={`${DOCUMENT_LIST_COLS_CLASS} sticky top-0 z-10 py-2 border-b border-border/50 bg-muted/30 backdrop-blur-sm`}
                      data-testid={testIds.documentList.listHeader}
                      role="row"
                    >
                      {(
                        [
                          { column: "name" as const, align: "start", hide: "" },
                          { column: "type" as const, align: "end", hide: "hidden sm:inline-flex" },
                          { column: "modified" as const, align: "end", hide: "" },
                          { column: "lines" as const, align: "end", hide: "hidden md:inline-flex" },
                          { column: "size" as const, align: "end", hide: "hidden md:inline-flex" },
                          { column: "author" as const, align: "end", hide: "hidden md:inline-flex" },
                        ] as const
                      ).map(({ column, align, hide }) => {
                        const active = sortBy === column
                        const SortIcon = sortDir === "asc" ? ChevronUp : ChevronDown
                        return (
                          <button
                            key={column}
                            type="button"
                            onClick={() => handleSortColumnClick(column)}
                            className={`${hide} inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide transition-colors ${
                              align === "end" ? "justify-self-end" : "justify-self-start"
                            } ${
                              active
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                            aria-label={`Sort by ${getSortColumnLabel(column)}${
                              active ? `, ${sortDir === "asc" ? "ascending" : "descending"}` : ""
                            }`}
                            aria-sort={
                              active
                                ? sortDir === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                            data-testid={testIds.documentList.sortColumn(column)}
                          >
                            <span>{getSortColumnLabel(column)}</span>
                            {active ? (
                              <SortIcon className="h-3 w-3 shrink-0 text-foreground/70" aria-hidden />
                            ) : (
                              <span className="h-3 w-3 shrink-0" aria-hidden />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {filteredOrderedItems.map((item, index) => {
                    if (item.kind === "folder") {
                      const { folder } = item
                      const updatedAtLabel =
                        folder.maxUpdatedAt != null && folder.maxUpdatedAt !== ""
                          ? formatTimeAgo(new Date(folder.maxUpdatedAt))
                          : undefined
                      return (
                        <FolderListRow
                          key={folder.path}
                          folder={folder}
                          color={workspaceColor}
                          urlCollision={folder.urlCollision === true}
                          isSelected={selectedItem?.kind === "folder" && selectedItem.path === folder.path}
                          onClick={() => setSelectedItem(folderSelection(folder))}
                          onDoubleClick={(e) => {
                            onFolderClick?.(folder.path, e)
                            window.dispatchEvent(
                              new CustomEvent("show-folder-in-browse", {
                                detail: { folderPath: folder.path },
                              }),
                            )
                          }}
                          onContextMenu={onFolderContextMenu}
                          updatedAtLabel={updatedAtLabel}
                        />
                      )
                    }
                    const doc = item.doc
                    const uniqueKey = `${doc.id}-${index}`
                    return (
                      <DocumentListRow
                        key={uniqueKey}
                        document={doc}
                        urlCollision={
                          folders.some(
                            (folder) =>
                              folder.urlCollision &&
                              folder.name ===
                                documentUrlStem(doc.filePath || doc.path)
                          )
                        }
                        isSelected={selectedItem?.kind === "document" && selectedItem.doc.id === doc.id}
                        onClick={() => setSelectedItem({ kind: "document", doc })}
                        onDoubleClick={(e) => {
                          const openInNewTab = e.metaKey || e.ctrlKey
                          onDocumentClick?.(doc.id, openInNewTab)
                        }}
                        onContextMenu={onDocumentContextMenu}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {/* Infinite scroll trigger & loading states */}
            {!loading && (
              <LoadMoreTrigger
                ref={loadMoreRef}
                loading={loadingMore}
                hasMore={hasMore}
                error={error}
                onRetry={loadMoreDocuments}
                currentCount={documents.length}
                totalCount={totalDocuments}
                data-testid={testIds.documentList.loadMore}
              />
            )}
          </>
        )}
      </div>
        </Panel>
        {preview ? (
          <>
            <SecondaryPreviewResizeHandle />
            <Panel
              defaultSize={30}
              minSize={20}
              maxSize={55}
              className="overflow-hidden"
              data-testid={testIds.documentList.previewPane}
            >
              {preview}
            </Panel>
          </>
        ) : null}
      </PanelGroup>
    </div>
    {backgroundMenu ? (
      <EntityContextMenu
        x={backgroundMenu.x}
        y={backgroundMenu.y}
        onClose={() => setBackgroundMenu(null)}
        entries={backgroundMenuEntries}
        data-testid={testIds.documentList.backgroundMenu}
      />
    ) : null}
    </IndexSurfaceShell>
  )
}
